import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { createSelector } from 'reselect';

import { CampaignStatus, ModalTypes, Urls, RequestState, ReportTypes } from 'constants/constants';
import { rangeToString } from 'utils/time';
import { withPolling } from 'utils/components';

import Brand, { selectBrands } from 'store/models/Brand';
import Campaign, { selectAllCampaigns, isCampaignPaused } from 'store/models/Campaign';
import Link, { selectLinks } from 'store/models/Link';
import Scene, { selectScenes } from 'store/models/Scene';
import Overlay, { selectOverlays } from 'store/models/Overlay';
import Stats, { selectBrandStats } from 'store/models/Stats';
import RouteState from 'store/RouteState';
import UIState, { addUIViewReducer } from 'store/UIState';
import AppState, { selectAppDateRange } from 'store/AppState';

import { ContentCenter, Title, Header, HeaderLeft, HeaderRight, Breadcrumbs } from 'components/Content';
import { BrandList } from 'components/BrandItem';
import { Button, ButtonList, AddButton, CircleAddButton, DownloadIconButton } from 'components/Button';
import Tooltip from 'components/Tooltip';
import DateRange, { DateRangeButton } from 'components/DateRange';
import EmptyItem from 'components/EmptyItem';
import _ from 'lodash';



// ======================
// STORE
// ======================
const SHOW_CAMPAIGNS = 'SHOW_CAMPAIGNS';

addUIViewReducer('brandsScreen', (state, action) => {
  if (action.url && action.result !== RequestState.SUCCESS)
    return state;

  switch(action.type) {
    case SHOW_CAMPAIGNS:
      return {
        ...state,
        showCampaigns: {
          ...state?.showCampaigns,
          [action.brandId]: action.visible,
        }
      }
  }
  return state;
});

const selectShowCampaigns = (state, brandId) => state.ui.views?.brandsScreen?.showCampaigns ?? {};

const showCampaign = (brandId, visible) => ({
  type: SHOW_CAMPAIGNS,
  brandId,
  visible,
})


const actions = {
  fetchData: () => async (dispatch, getState) => {
    let { dates } = selectAppDateRange(getState());
    dispatch(Brand.actions.fetchBrands());
    dispatch(Campaign.actions.fetchCampaigns());
    dispatch(Overlay.actions.fetchOverlays());
    dispatch(Link.actions.fetchLinks());
    dispatch(Scene.actions.fetchScenes());
    dispatch(Stats.actions.fetchBrandStats(dates.from.valueOf(), dates.to.valueOf()));
  },
  
  refreshStats: (dates) => async (dispatch, getState) => {
    await dispatch(Stats.actions.fetchBrandStats(dates.from.valueOf(), dates.to.valueOf()))
  },

  toggleShowCampaigns: brandId => (dispatch, getState) => 
    dispatch(showCampaign(brandId, !(selectShowCampaigns(getState())?.[brandId] ?? false))),

  showLinkUrlModal: (campaignId, linkId) => dispatch => 
    dispatch(UIState.actions.showModal(ModalTypes.LINK_URLS, { campaignId, linkId })),
  campaignDelete: Campaign.actions.deleteCampaign,
  deleteBrand: Brand.actions.deleteBrand,
  navigate: RouteState.actions.navigate,
  showModal: UIState.actions.showModal,
}

const selectors = createSelector(
  selectBrands,
  selectAllCampaigns,
  state => selectLinks(state),
  state => selectScenes(state),
  selectBrandStats,
  selectAppDateRange,
  selectShowCampaigns,
  selectOverlays,
  (brands, campaigns, links, scenes, brandStats, dateRange, showCampaigns, overlays) => ({ 
    brands: brands
      |> _.orderBy(#, brand => _.values(campaigns)
        |> _.filter(#, { brandId: brand._id })
        |> _.every(#, isCampaignPaused)
          ? -1000000000000 + (brandStats?.[brand._id]?.totalMediaValue ?? 0)
          : brandStats?.[brand._id]?.totalMediaValue,
      'desc'),
    brandStats,
    campaignsByBrand: campaigns
      |> _.sortBy(#, v => v.status === CampaignStatus.ACTIVE ? 0 : 10)
      |> _.groupBy(#, 'brandId'),
    linksByCampaign: campaigns
      |> _.keyBy(#, '_id')
      |> _.mapValues(#, v => _.map(v.links, 'linkId'))
      |> _.mapValues(#, v => _.pick(_.keyBy(links, '_id'), v)),
    scenesByCampaign: campaigns
      |> _.keyBy(#, '_id')
      |> _.mapValues(#, v => _.map(v.scenes, 'sceneId'))
      |> _.mapValues(#, v => _.pick(_.keyBy(scenes, '_id'), v)),
    dateRange,
    rangeLabel: rangeToString(dateRange.range, dateRange.dates),
    showCampaigns,
    overlays,
  }),
);

const withConnect = connect(selectors, actions);

// ======================
// COMPONENT
// ====================== 
class CampaignScreen extends React.Component {
  state = {
    loading: false,
  }

  componentDidMount = async () => {
    this.setState({ loading: true });
    await this.props.fetchData();
    this.setState({ loading: false });
  }

  componentWillUnmount() {
    this.props.stopAllPolling();
  }

  onDateChange = async (_, dates) => {
    this.setState({ loading: true });
    await this.props.refreshStats(dates);
    this.setState({ loading: false });
  }

  onExportView = () => this.props.showModal(ModalTypes.EXPORT, { report: ReportTypes.BRANDS })
  
  onBrandCreate = () => this.props.showModal(ModalTypes.BRAND_EDIT, { 
    onSuccess: async () => await this.props.refreshStats(this.props.dateRange.dates),
  })
  onBrandEdit = brandId => this.props.showModal(ModalTypes.BRAND_EDIT, { brandId })
  onBrandDelete = brandId => this.props.deleteBrand(brandId)

  onCampaignCreate = brandId => this.props.navigate(Urls.CAMPAIGN_NEW_ROUTE, { brandId, campaignId: 'create' });
  onCampaignEdit = (brandId, campaignId) => this.props.navigate(Urls.CAMPAIGN_EDIT_ROUTE, { brandId, campaignId });
  onCampaignDelete = async campaignId => { 
    await this.props.campaignDelete(campaignId);
    this.props.fetchData();
  }
  onLinkCreate = campaignId => this.props.navigate(Urls.LINK_NEW, { campaignId });
  onLinkUrls = (campaignId, linkId) => this.props.showLinkUrlModal(campaignId, linkId);
  onShowCampaigns = brandId => this.props.toggleShowCampaigns(brandId);


  render() {
    let { brands, brandStats, campaignsByBrand, linksByCampaign, scenesByCampaign, overlays, dateRange, showCampaigns } = this.props;
    let { loading } = this.state;
    let hasBrands = _.size(brands) !== 0;
    return (
      <ContentCenter className="campaigns">
        <Header split={true}>
          <HeaderLeft>
            <Title>
              Brands
              <Tooltip tooltip="New Brand">
                <CircleAddButton size="s" onClick={this.onBrandCreate} />
              </Tooltip>
            </Title>
          </HeaderLeft>
          <HeaderRight>
            <DateRange disabled={loading} onDateChange={this.onDateChange} />
            <DownloadIconButton color="light" disabled={loading} onClick={this.onExportView} />
          </HeaderRight>
        </Header>

        { !hasBrands && !loading &&
        <EmptyItem className="no-channels" size="xl">
          <h1>Create A Brand</h1>
          <h2>Brands hold all the slides and link campaigns, and let you deliver reports specific to your sponsor</h2>
          <AddButton onClick={this.onBrandCreate} size="l">Create A New Brand</AddButton>
        </EmptyItem>}

        <BrandList 
          header={true}
          brands={brands}
          brandStats={brandStats}
          campaignsByBrand={campaignsByBrand}
          linksByCampaign={linksByCampaign}
          scenesByCampaign={scenesByCampaign}
          showCampaignsByBrand={showCampaigns}
          overlays={overlays}
          onEditBrand={this.onBrandEdit}
          onLinkCreate={this.onLinkCreate}
          onLinkUrls={this.onLinkUrls}
          onDeleteBrand={this.onBrandDelete}
          onShowCampaigns={this.onShowCampaigns}
          onAddCampaign={this.onCampaignCreate}
          onEditCampaign={this.onCampaignEdit}
          onDeleteCampaign={this.onCampaignDelete} />
      </ContentCenter>)
  }
}
export default CampaignScreen
  |> withConnect
  |> withPolling;